import {
  ICreateSubscriptionByPatientResponse,
  IDailyExerciseQuery,
  IDailyExerciseTableData,
  StageActionEnum,
} from "./../../shared/types/exercise";
import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { PathBuilder, RequestTypes, axiosBaseQuery } from "../../utils";
import {
  IMessageResponse,
  ISubscription,
  ISubscriptionPlansWithPriceResponse,
  credential,
} from "../../shared";
import { IExercisesTableData } from "../../pages/AdminPanel/Exercises/PatientExercisesTable/patientExercisesTable.interface";
import { ReducerKeys } from "../types";
import { userApi } from "./userApi";

export const dailyExerciseApi = createApi({
  reducerPath: ReducerKeys.DAILY_EXERCISES_API_REDUCER_KEY,
  baseQuery: axiosBaseQuery({
    baseUrl: credential.apiUrl as string,
  }),
  tagTypes: [
    ReducerKeys.DAILY_EXERCISES_API_REDUCER_KEY,
    ReducerKeys.PAYMENTS_API_REDUCER_KEY,
  ],
  endpoints: (build) => ({
    fetchDailyExercise: build.query<
      IDailyExerciseTableData[],
      IDailyExerciseQuery
    >({
      query: (params) => ({
        url: PathBuilder.dailyExerciseApi.fetchDailyExercise(),
        method: RequestTypes.get,
        params,
      }),
      providesTags: () => [ReducerKeys.DAILY_EXERCISES_API_REDUCER_KEY],
    }),
    updateDailyExercise: build.mutation<IMessageResponse, IExercisesTableData>({
      query: (params) => {
        const { _id: id, ...newParams } = params;
        return {
          url: PathBuilder.dailyExerciseApi.updateDailyExercise(id as string),
          method: RequestTypes.put,
          data: newParams,
        };
      },
      invalidatesTags: [ReducerKeys.DAILY_EXERCISES_API_REDUCER_KEY],
    }),
    fetchDailyPatientExercise: build.query<IExercisesTableData, void>({
      query: () => ({
        url: PathBuilder.dailyExerciseApi.fetchDailyPatientExercise(),
        method: RequestTypes.get,
      }),
      providesTags: () => [ReducerKeys.DAILY_EXERCISES_API_REDUCER_KEY],
    }),
    updateDailyPatientExercise: build.mutation<
      IMessageResponse,
      IExercisesTableData
    >({
      query: (params) => {
        const { _id: id, ...newParams } = params;
        return {
          url: PathBuilder.dailyExerciseApi.updateDailyPatientExercise(
            id as string,
          ),
          method: RequestTypes.put,
          data: newParams,
        };
      },
      invalidatesTags: [ReducerKeys.DAILY_EXERCISES_API_REDUCER_KEY],
    }),
    createSubscriptionByPatient: build.mutation<
      ICreateSubscriptionByPatientResponse,
      {
        serviceId: string;
        subscriptionPlanId: string;
      }
    >({
      query: (data) => ({
        url: PathBuilder.dailyExerciseApi.createSubscriptionByPatient(),
        method: RequestTypes.post,
        data: data,
      }),
      invalidatesTags: [
        ReducerKeys.DAILY_EXERCISES_API_REDUCER_KEY,
        ReducerKeys.PAYMENTS_API_REDUCER_KEY,
      ],
    }),
    fetchPatientSubscription: build.query<ISubscription, string>({
      query: (id) => ({
        url: PathBuilder.dailyExerciseApi.fetchPatientSubscription(id),
        method: RequestTypes.get,
      }),
      providesTags: () => [ReducerKeys.PAYMENTS_API_REDUCER_KEY],
    }),
    createSubscriptionByStaff: build.mutation<
      ICreateSubscriptionByPatientResponse,
      {
        patientId: string;
        serviceId: string;
        subscriptionPlanId: string;
      }
    >({
      query: (data) => ({
        url: PathBuilder.dailyExerciseApi.createSubscriptionByStaff(),
        method: RequestTypes.post,
        data: data,
      }),
    }),
    fetchPatientSubscriptionByStaff: build.query<ISubscription, string>({
      query: (id) => ({
        url: PathBuilder.dailyExerciseApi.fetchPatientSubscriptionByStaff(id),
        method: RequestTypes.get,
      }),
      providesTags: () => [ReducerKeys.PAYMENTS_API_REDUCER_KEY],
    }),
    fetchPatientSubscriptionPlans: build.query<
      {
        serviceId: string;
        patientId: string;
        plans: ISubscriptionPlansWithPriceResponse[];
      },
      string
    >({
      query: (serviceId) => ({
        url: PathBuilder.dailyExerciseApi.fetchPatientSubscriptionPlans(),
        method: RequestTypes.get,
        params: { serviceId: serviceId },
      }),
      providesTags: () => [ReducerKeys.DAILY_EXERCISES_API_REDUCER_KEY],
    }),
    fetchPatientSubscriptionPlansByStaff: build.query<
      {
        serviceId: string;
        patientId: string;
        plans: ISubscriptionPlansWithPriceResponse[];
      },
      { serviceId: string; patientId: string }
    >({
      query: (data) => ({
        url: PathBuilder.dailyExerciseApi.fetchPatientSubscriptionPlansByStaff(),
        method: RequestTypes.get,
        params: data,
      }),
      providesTags: () => [ReducerKeys.DAILY_EXERCISES_API_REDUCER_KEY],
    }),
    processStageActionByPatient: build.mutation<
      IMessageResponse,
      { action: keyof typeof StageActionEnum }
    >({
      query: (data) => ({
        url: PathBuilder.dailyExerciseApi.processStageActionByPatient(),
        method: RequestTypes.post,
        data: data,
      }),
      invalidatesTags: [ReducerKeys.DAILY_EXERCISES_API_REDUCER_KEY],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(
            userApi.util.invalidateTags([ReducerKeys.USERS_API_REDUCER_KEY]),
          );
        } catch {}
      },
    }),
    processStageActionByStaff: build.mutation<
      IMessageResponse,
      { patientId: string; action: keyof typeof StageActionEnum }
    >({
      query: (data) => ({
        url: PathBuilder.dailyExerciseApi.processStageActionByStaff(),
        method: RequestTypes.post,
        data: data,
      }),
      invalidatesTags: [ReducerKeys.DAILY_EXERCISES_API_REDUCER_KEY],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(
            userApi.util.invalidateTags([ReducerKeys.USERS_API_REDUCER_KEY]),
          );
        } catch {}
      },
    }),
  }),
});
